import React from "react";
import { Info, ShieldCheck } from "lucide-react";
import { Card, CardContent } from "src/components/ui/Card";
import { Label } from "src/components/ui/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/Select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import { SessionSchedulingType } from "src/services/sessionsService";

interface MeetingProviderCardProps {
  schedulingType: SessionSchedulingType;
  scheduledDatesCount: number;
}

export const MeetingProviderCard: React.FC<MeetingProviderCardProps> = ({
  schedulingType,
  scheduledDatesCount,
}) => {
  return (
    <Card className="border border-slate-200 rounded-xl shadow-xs overflow-hidden bg-white">
      <div className="bg-[#faf9fe] px-5 py-3.5 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900">
            Provider Provisioning
          </h3>
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="text-slate-400 hover:text-slate-600 transition p-0.5 rounded-full inline-flex items-center cursor-help"
                  aria-label="Provisioning pipeline details"
                >
                  <Info className="w-3.5 h-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-xs p-2.5 bg-slate-900 text-slate-100 shadow-lg">
                On save, background workers idempotently queue discrete provisioning jobs in meeting_operations. Join URLs dispatch once in ready state.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      <CardContent className="p-5 space-y-4">
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-slate-700">Meeting Provider Engine</Label>
          <Select defaultValue="teams">
            <SelectTrigger className="h-9 text-xs font-medium rounded-lg border-slate-300 bg-white">
              <SelectValue placeholder="Select Provider" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="teams">Microsoft Teams (Platform Managed)</SelectItem>
              <SelectItem value="zoom" disabled>Zoom Video (Adapter Pending)</SelectItem>
              <SelectItem value="meet" disabled>Google Meet (Adapter Pending)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500">
            Uses platform-managed account credentials. Tutors and students authenticate with rfLearn SSO.
          </p>
        </div>

        {/* Quiet metadata footer */}
        <div className="pt-3 border-t border-slate-100 flex items-center gap-1.5 text-xs text-slate-500 font-medium">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
          <span>Automated room creation on save</span>
        </div>
      </CardContent>
    </Card>
  );
};

